import { Extension } from '@tiptap/core';
import type { Node as PMNode } from '@tiptap/pm/model';
import { Plugin, PluginKey, type EditorState, type Transaction } from '@tiptap/pm/state';
import { Decoration, DecorationSet } from '@tiptap/pm/view';
import { ySyncPluginKey } from '@tiptap/y-tiptap';
import { expandCapture, isOpen, statusFromChar, toIsoDate, uuidv7 } from '@nexus/core';
import { applyTaskStatus } from '../tasks';

/** Changes arriving from Yjs (other views, other devices) are not user edits. */
const fromSync = (tr: Transaction) =>
  (tr.getMeta(ySyncPluginKey) as { isChangeOrigin?: boolean } | undefined)?.isChangeOrigin === true;

function taskItems(doc: PMNode): Map<string, PMNode> {
  const out = new Map<string, PMNode>();
  doc.descendants((node) => {
    if (node.type.name === 'taskItem' && node.attrs.taskId)
      out.set(node.attrs.taskId as string, node);
  });
  return out;
}

/** Position of the task item the cursor is in, if any. */
function taskAtCursor(state: EditorState): number | null {
  const $from = state.selection.$from;
  for (let d = $from.depth; d > 0; d--)
    if ($from.node(d).type.name === 'taskItem') return $from.before(d);
  return null;
}

/**
 * The quick-capture shorthand in a task line (`@morgen 14u`, `@vrijdag`, `!!`) becomes
 * `📅 date` / a priority once the cursor leaves the line, so it is fixed to a real date
 * (and "morgen" does not move along every day). Text nodes are rewritten one by one,
 * so links, tags and formatting around them stay.
 */
function expandShorthand(tr: Transaction, state: EditorState, pos: number, today: string) {
  const node = state.doc.nodeAt(pos);
  if (node?.type.name !== 'taskItem') return;
  const para = node.firstChild;
  if (!para || para.type.name !== 'paragraph') return;
  const edits: { from: number; to: number; text: string; child: PMNode }[] = [];
  para.forEach((child, offset) => {
    if (!child.isText || !/(^|\s)[@!]/.test(child.text!)) return;
    const text = child.text!;
    const expanded = expandCapture(text, today);
    if (expanded === text.replace(/\s+/g, ' ').trim()) return; // no shorthand found
    const lead = /^\s*/.exec(text)![0];
    const trail = /\s*$/.exec(text)![0];
    const from = pos + 2 + offset;
    edits.push({ from, to: from + text.length, text: `${lead}${expanded}${trail}`, child });
  });
  for (const e of edits.reverse())
    tr.replaceWith(
      tr.mapping.map(e.from),
      tr.mapping.map(e.to),
      state.schema.text(e.text, e.child.marks),
    );
}

/**
 * Task behaviour in the editor, matching the task views:
 * - every task item has a unique id (new, pasted, duplicated),
 * - checking a box sets the status character and `✅ date`; unchecking removes it,
 * - completing a recurring task inserts the next occurrence,
 * - due dates are highlighted (overdue / today),
 * - `@morgen`-style shorthand becomes a due date when the cursor leaves the line.
 */
export const TaskBehaviour = Extension.create({
  name: 'taskBehaviour',
  addProseMirrorPlugins() {
    return [
      new Plugin({
        key: new PluginKey('taskBehaviour'),
        appendTransaction(transactions, oldState, newState) {
          if (transactions.some(fromSync)) return null;
          const tr = newState.tr;
          // The line the cursor was on, mapped through this change (Enter splits it).
          const was = taskAtCursor(oldState);
          if (was !== null) {
            const pos = transactions.reduce((p, t) => t.mapping.map(p, 1), was);
            if (pos !== taskAtCursor(newState))
              expandShorthand(tr, newState, pos, toIsoDate(new Date()));
          }
          if (!transactions.some((t) => t.docChanged)) return tr.docChanged ? tr : null;
          const before = taskItems(oldState.doc);
          const seen = new Set<string>();
          const toggled: { pos: number; node: PMNode; old: PMNode }[] = [];
          newState.doc.descendants((node, pos) => {
            if (node.type.name !== 'taskItem') return;
            const id = node.attrs.taskId as string | null;
            if (!id || seen.has(id)) {
              tr.setNodeAttribute(tr.mapping.map(pos), 'taskId', uuidv7());
              return;
            }
            seen.add(id);
            const old = before.get(id);
            if (old && old.attrs.checked !== node.attrs.checked) toggled.push({ pos, node, old });
          });
          const today = toIsoDate(new Date());
          // Replace from the end so earlier positions stay valid.
          for (const { pos, node, old } of toggled.reverse()) {
            const status = node.attrs.checked ? 'done' : 'todo';
            const items = applyTaskStatus(
              { ...node.toJSON(), attrs: { ...old.attrs } },
              status,
              today,
            );
            const nodes = items.map((j) => newState.schema.nodeFromJSON(j));
            tr.replaceWith(tr.mapping.map(pos), tr.mapping.map(pos + node.nodeSize), nodes);
          }
          return tr.docChanged ? tr : null;
        },
        props: {
          decorations(state) {
            const today = toIsoDate(new Date());
            const decorations: Decoration[] = [];
            state.doc.descendants((node, pos) => {
              if (node.type.name !== 'taskItem') return;
              const done =
                node.attrs.checked || !isOpen(statusFromChar((node.attrs.status as string) ?? ' '));
              const para = node.firstChild;
              if (!para) return false;
              para.forEach((child, offset) => {
                if (!child.isText) return;
                for (const m of child.text!.matchAll(
                  /📅️?\s*(\d{4}-\d{2}-\d{2})(?:[\sT]\d{1,2}:\d{2})?/gu,
                )) {
                  const from = pos + 2 + offset + m.index;
                  const cls = done
                    ? ''
                    : m[1]! < today
                      ? ' is-overdue'
                      : m[1] === today
                        ? ' is-today'
                        : '';
                  decorations.push(
                    Decoration.inline(from, from + m[0].length, { class: `nx-task-due${cls}` }),
                  );
                }
                const reminder =
                  /⏰\S*\s*(?:\d{4}-\d{2}-\d{2}(?:[\sT]\d{1,2}:\d{2})?|-?\d+\s*(?:min|m|h|u|d|w))/gu;
                for (const m of child.text!.matchAll(reminder)) {
                  const from = pos + 2 + offset + m.index;
                  decorations.push(
                    Decoration.inline(from, from + m[0].length, { class: 'nx-task-reminder' }),
                  );
                }
              });
              return true;
            });
            return DecorationSet.create(state.doc, decorations);
          },
        },
      }),
    ];
  },
});
